import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MinusIcon, PlusIcon, Trash2Icon, CheckIcon } from 'lucide-react';
import { formatPrice, formatPriceExact, vatPart, VAT_LABEL } from '../data/smaken';
import { useRequest } from '../CartContext';
import { Reveal } from '../components/Reveal';
import { ArrowMark, Note } from '../components/Marks';
type Step = 1 | 2 | 3;
type DietKey = 'glutenfri' | 'vegansk' | 'laktosfri';

/** Pickup window when the guest hasn't asked for separate times per place. */
const PICKUP_WINDOW_MINUTES = 60;
const DIETS: {
  key: DietKey;
  label: string;
  note: string;
}[] = [{
  key: 'glutenfri',
  label: 'Glutenfri',
  note: 'Bakas och packas separat'
}, {
  key: 'vegansk',
  label: 'Vegansk',
  note: 'Helt växtbaserat, inget mejeri'
}, {
  key: 'laktosfri',
  label: 'Laktosfri',
  note: 'Mejeri byts, smaken behålls'
}];

/** "11.30" + 60 min → "12.30". Returns '' until a time is picked. */
function addMinutes(time: string, minutes: number): string {
  if (!/^\d{2}:\d{2}$/.test(time)) return '';
  const [h, m] = time.split(':').map(Number);
  const end = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(end / 60)).padStart(2, '0')}.${String(end % 60).padStart(2, '0')}`;
}
export function RequestPage() {
  const {
    lines,
    setQuantity,
    remove,
    total,
    clear
  } = useRequest();
  const [step, setStep] = useState<Step>(1);
  const [type, setType] = useState<'foretag' | 'privat'>('foretag');
  const [logistics, setLogistics] = useState<'leverans' | 'upphamtning'>('leverans');
  const [frequency, setFrequency] = useState<'engang' | 'lopande'>('engang');
  const [cadence, setCadence] = useState('Varje vecka');
  const [showErrors, setShowErrors] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [diets, setDiets] = useState<Record<DietKey, {
    on: boolean;
    count: string;
  }>>({
    glutenfri: {
      on: false,
      count: ''
    },
    vegansk: {
      on: false,
      count: ''
    },
    laktosfri: {
      on: false,
      count: ''
    }
  });
  const [splitPickup, setSplitPickup] = useState(false);
  const [pickupTimes, setPickupTimes] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    name: '',
    org: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    address: '',
    notes: ''
  });
  const set = (key: keyof typeof form, value: string) => setForm(p => ({
    ...p,
    [key]: value
  }));

  /** Every distinct place in the basket — pickup happens at each of them. */
  const places = useMemo(() => {
    const seen = new Map<string, string>();
    lines.forEach(l => seen.set(l.placeSlug, l.placeName));
    return [...seen].map(([slug, name]) => ({
      slug,
      name
    }));
  }, [lines]);
  const missing = useMemo(() => {
    const required: (keyof typeof form)[] = ['name', 'email', 'date', 'time'];
    if (logistics === 'leverans') required.push('address');
    return required.filter(k => !form[k].trim());
  }, [form, logistics]);
  const activeDiets = DIETS.filter(d => diets[d.key].on);
  const emailValid = /\S+@\S+\.\S+/.test(form.email);
  const canContinue = missing.length === 0 && emailValid;

  /** Prices are incl. VAT; this is the VAT share of the total. */
  const vat = vatPart(total);
  const hasOnRequest = lines.some(l => l.onRequest);
  const submit = () => {
    setReference(`SMÅ-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`);
    clear();
  };

  /* -------------------------------- Confirmed -------------------------------- */

  if (reference) {
    return <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8" data-id="element-1124" data-mp="1bm9el97tihu9">
        <Reveal data-id="element-1125" data-mp="1gnyogljb0pgr">
          <CheckIcon className="mx-auto h-12 w-12 text-rod" aria-hidden="true" data-id="element-1126" data-mp="1gnf6j4tnm12q" />
          <h1 className="display-xl mt-6 text-4xl uppercase sm:text-6xl" data-id="element-1127" data-mp="1knhaxlqrx8lp">Skickat.</h1>
          <p className="mt-4 text-lg leading-relaxed text-black/70" data-id="element-1128" data-mp="3if209e4xka2w">
            Vi läser förfrågan själva och återkommer med bekräftelse och pris inom 24 timmar.
          </p>
          <div className="mt-8 border-2 border-black px-6 py-5" data-id="element-1129" data-mp="2r5p6g3kkfutw">
            <p className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1130" data-mp="2r3vxghmhcyiq">Referens</p>
            <p className="display-xl mt-1 text-2xl" data-id="element-1131" data-mp="2n3u5x1agllh6">{reference}</p>
          </div>
          <Link to="/catering" className="mt-8 inline-block bg-black px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1132" data-mp="313cz8nym2s9f">
            Tillbaka till catering
          </Link>
        </Reveal>
      </div>;
  }

  /* ---------------------------------- Empty ---------------------------------- */

  if (lines.length === 0) {
    return <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8" data-id="element-1133" data-mp="2deal3q36rhjo">
        <h1 className="display-xl text-4xl uppercase sm:text-6xl" data-id="element-1134" data-mp="2dc7kkc4eq60p">Tom förfrågan.</h1>
        <Note className="mt-4 block" data-id="element-1135" data-mp="1pn55nc15qr9w">Börja med att lägga till något gott.</Note>
        <Link to="/catering" className="mt-8 inline-block bg-rod px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1136" data-mp="1kolemx7pipao">
          Se all catering
        </Link>
      </div>;
  }
  const inputClass = (invalid: boolean) => `w-full border-b bg-transparent py-3 font-display text-xl outline-none transition-colors duration-150 ease-out placeholder:text-black/25 focus:border-rod sm:text-2xl ${invalid ? 'border-rod' : 'border-black/25'}`;
  const labelClass = 'text-[11px] font-bold uppercase tracking-wide2 text-black/45';
  const choice = (active: boolean) => `border-2 p-6 text-left transition-colors duration-150 ease-out ${active ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`;
  const steps = [{
    id: 1 as Step,
    label: 'Upplägg'
  }, {
    id: 2 as Step,
    label: 'Uppgifter'
  }, {
    id: 3 as Step,
    label: 'Granska'
  }];
  return <div className="mx-auto max-w-page px-5 py-12 md:px-8 md:py-16" data-id="element-1137" data-mp="2znhavd3dr1nd">
      <Reveal data-id="element-1138" data-mp="1570v9ieukxo7">
        <h1 className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-1139" data-mp="155fydm5292n6">Din förfrågan</h1>
        <p className="mt-3 text-lg text-black/60" data-id="element-1140" data-mp="1074nd8314m3d">
          Tre steg. Vi svarar med bekräftelse och pris inom 24 timmar.
        </p>

        <ol className="mt-10 flex items-center gap-4 border-b-2 border-black pb-5" data-id="element-1141" data-mp="vf4u1yhgsrfr">
          {steps.map((s, i) => <li key={s.id} className="flex items-center gap-4" data-id="element-1142" data-mp="rd01l32r60up" data-mpi={i}>
              <button onClick={() => s.id < step && setStep(s.id)} disabled={s.id > step} className={`flex items-baseline gap-2.5 text-[12px] font-bold uppercase tracking-wide2 ${s.id <= step ? 'text-black' : 'text-black/30'}`} data-id="element-1143" data-mp="mgsuv82j8lbp">
                <span className={`display-xl text-2xl ${s.id <= step ? 'text-rod' : 'text-black/25'}`} data-id="element-1144" data-mp="8ibbfmjntuzl">
                  0{s.id}
                </span>
                <span className="hidden sm:inline" data-id="element-1145" data-mp="22tzt3wha0bno">{s.label}</span>
              </button>
              {i < steps.length - 1 && <span className="h-px w-6 bg-black/25 sm:w-10" data-id="element-1146" data-mp="3kr35entvyha3" />}
            </li>)}
        </ol>
      </Reveal>

      <div className="mt-12 grid gap-14 lg:grid-cols-[minmax(0,1.3fr),minmax(0,1fr)] lg:gap-20" data-id="element-1147" data-mp="3pp4p9az4ykmm">
        <div data-id="element-1148" data-mp="3uihbxfk98hcq">
          {step === 1 && <Reveal className="space-y-10" data-id="element-1149" data-mp="2dn6vdmyky4u">
              <section data-id="element-1150" data-mp="2ddgvq1wyqz5">
                <h2 className={labelClass} data-id="element-1151" data-mp="2fzpzu4gdejh">Vem beställer?</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1152" data-mp="31ubqeqldmobu">
                  <button onClick={() => setType('foretag')} className={choice(type === 'foretag')} data-id="element-1153" data-mp="36rtt3z8sw7be">
                    <p className="font-display text-2xl uppercase" data-id="element-1154" data-mp="3at6xe4w8w2g7">Företag</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1155" data-mp="3fsrwcfbf0asx">Kontor, konferens, kickoff</p>
                  </button>
                  <button onClick={() => setType('privat')} className={choice(type === 'privat')} data-id="element-1156" data-mp="q3punhjubhxn">
                    <p className="font-display text-2xl uppercase" data-id="element-1157" data-mp="v2an2usuoxbj">Privat</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1158" data-mp="v4do09vjkav1">Bröllop, fest, middag</p>
                  </button>
                </div>
              </section>

              <section data-id="element-1159" data-mp="1s744x41xtbm">
                <h2 className={labelClass} data-id="element-1160" data-mp="1sqmgexm6jq6">Hur vill ni ha det?</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1161" data-mp="fjc6w82pv1q6">
                  <button onClick={() => setLogistics('leverans')} className={choice(logistics === 'leverans')} data-id="element-1162" data-mp="aru89qo61d8d">
                    <p className="font-display text-2xl uppercase" data-id="element-1163" data-mp="pfypsrbhfipk">Leverans</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1164" data-mp="khdqqv7yv52p">Vi kör ut och dukar upp</p>
                  </button>
                  <button onClick={() => setLogistics('upphamtning')} className={choice(logistics === 'upphamtning')} data-id="element-1165" data-mp="yhoj62oxcrpz">
                    <p className="font-display text-2xl uppercase" data-id="element-1166" data-mp="308jhb16lt2zv">Upphämtning</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1167" data-mp="30am5dfo74m1y">Ni hämtar hos aktören</p>
                  </button>
                </div>
              </section>

              <section data-id="element-1168" data-mp="2m2rc83e5x2oe">
                <h2 className={labelClass} data-id="element-1169" data-mp="2m3j536gdkmaz">En gång eller löpande?</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1170" data-mp="2q4unos0u079o">
                  <button onClick={() => setFrequency('engang')} className={choice(frequency === 'engang')} data-id="element-1171" data-mp="2cd7r0r2z8wqd">
                    <p className="font-display text-2xl uppercase" data-id="element-1172" data-mp="2hc16gp1tqxt6">Engångs</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1173" data-mp="2h9y4d6nede83">Ett tillfälle, ett datum</p>
                  </button>
                  <button onClick={() => setFrequency('lopande')} className={choice(frequency === 'lopande')} data-id="element-1174" data-mp="27e3i81jyt10k">
                    <p className="font-display text-2xl uppercase" data-id="element-1175" data-mp="1tevfiyolfarm">Löpande</p>
                    <p className="mt-1 text-sm opacity-70" data-id="element-1176" data-mp="1tgokm9jk5mxi">Samma leverans om och om igen</p>
                  </button>
                </div>

                {frequency === 'lopande' && <div className="mt-5 border-l-2 border-rod pl-5" data-id="element-1177" data-mp="3nqa9jromzwd6">
                    <label htmlFor="r-interval" className={labelClass} data-id="element-1178" data-mp="3sq4u7dhtjpps">
                      Hur ofta?
                    </label>
                    <select id="r-interval" value={cadence} onChange={e => setCadence(e.target.value)} className="mt-1 w-full cursor-pointer border-b border-black/25 bg-transparent py-2 font-display text-xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-1179" data-mp="3qz7f5vadx59o">
                      {['Varje vecka', 'Varannan vecka', 'Varje månad', 'Varje kvartal'].map(o => <option key={o} data-id="element-1180" data-mp="ij4gug55wsy0">{o}</option>)}
                    </select>
                    <p className="mt-2 text-sm leading-relaxed text-black/60" data-id="element-1181" data-mp="4nscri549q4f">
                      Datumet nedan blir er första leverans. Ni kan pausa eller ändra när ni vill.
                    </p>
                  </div>}
              </section>

              <button onClick={() => setStep(2)} className="group inline-flex items-center gap-3 bg-black px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1182" data-mp="2ubjd0mqpxnbr">
                Vidare
                <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1183" data-mp="2vrex3lex2rs0" />
              </button>
            </Reveal>}

          {step === 2 && <Reveal className="space-y-8" data-id="element-1184" data-mp="233st4c1s1l4m">
              <div data-id="element-1185" data-mp="1y4g9e067cu69">
                <label htmlFor="r-name" className={labelClass} data-id="element-1186" data-mp="1y4y2wp1hcuah">
                  {type === 'foretag' ? 'Företagsnamn' : 'Namn'} *
                </label>
                <input id="r-name" value={form.name} onChange={e => set('name', e.target.value)} placeholder={type === 'foretag' ? 'Verket AB' : 'För- och efternamn'} className={inputClass(showErrors && !form.name.trim())} data-id="element-1187" data-mp="2cv5k71fr479l" />
              </div>

              {type === 'foretag' && <div data-id="element-1188" data-mp="3fdu27t84rn59">
                  <label htmlFor="r-org" className={labelClass} data-id="element-1189" data-mp="3kf181mqpqc9a">
                    Organisationsnummer
                  </label>
                  <input id="r-org" value={form.org} onChange={e => set('org', e.target.value)} placeholder="556123-4567" className={inputClass(false)} data-id="element-1190" data-mp="3p5hozgtqsg0e" />
                </div>}

              <div className="grid gap-8 sm:grid-cols-2" data-id="element-1191" data-mp="31gyecun7zr55">
                <div data-id="element-1192" data-mp="35i0f5fwscb98">
                  <label htmlFor="r-email" className={labelClass} data-id="element-1193" data-mp="35hh6yqwxt18e">
                    E-post *
                  </label>
                  <input id="r-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="namn@foretag.se" className={inputClass(showErrors && !emailValid)} data-id="element-1194" data-mp="3ahmvvzbto9ms" />
                </div>
                <div data-id="element-1195" data-mp="uvq6nw871s4q">
                  <label htmlFor="r-phone" className={labelClass} data-id="element-1196" data-mp="uwhpe6gozvdl">
                    Telefon
                  </label>
                  <input id="r-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="070 123 45 67" className={inputClass(false)} data-id="element-1197" data-mp="q36qcri4giw8" />
                </div>
                <div data-id="element-1198" data-mp="c7bbqxur9tyn">
                  <label htmlFor="r-date" className={labelClass} data-id="element-1199" data-mp="75c95q6ub12r">
                    Datum *
                  </label>
                  <input id="r-date" type="date" value={form.date} onChange={e => set('date', e.target.value)} className={inputClass(showErrors && !form.date.trim())} data-id="element-1200" data-mp="35bxuef1f5zx" />
                </div>
                <div data-id="element-1201" data-mp="24mt35638e6bf">
                  <label htmlFor="r-time" className={labelClass} data-id="element-1202" data-mp="24mb2lx9jd5xp">
                    {logistics === 'leverans' ? 'Exakt leveranstid' : 'Upphämtning från'} *
                  </label>
                  <input id="r-time" type="time" value={form.time} onChange={e => set('time', e.target.value)} className={inputClass(showErrors && !form.time.trim())} data-id="element-1203" data-mp="1vka30tqty7sv" />
                  <p className="mt-2 text-sm text-black/55" data-id="element-1204" data-mp="1qmidu7hkkzsd">
                    {logistics === 'leverans' ? 'Vi står på plats på minuten. Ange när det ska stå framme.' : 'Startpunkt för er upphämtning.'}
                  </p>
                </div>
              </div>

              {logistics === 'leverans' ? <div data-id="element-1205" data-mp="17uzbm2vddpre">
                  <label htmlFor="r-address" className={labelClass} data-id="element-1206" data-mp="17vii8nfkkrq4">
                    Leveransadress *
                  </label>
                  <input id="r-address" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Storgatan 1, 352 30 Växjö" className={inputClass(showErrors && !form.address.trim())} data-id="element-1207" data-mp="o9yzxjncopzm" />
                </div> : <section className="border-l-2 border-rod pl-5" data-id="element-1208" data-mp="1779pgcj6k51v">
                  <h2 className={labelClass} data-id="element-1209" data-mp="12986rspo27qx">Upphämtning</h2>

                  {places.length > 1 ? <>
                      <p className="mt-2 leading-relaxed text-black/70" data-id="element-1210" data-mp="1gye75k08q40e">
                        Er beställning ligger hos {places.length} verksamheter.{' '}
                        {splitPickup ? 'Ange en tid per ställe nedan.' : `Allt står klart att hämta mellan ${form.time.replace(':', '.') || '––.––'} och ${addMinutes(form.time, PICKUP_WINDOW_MINUTES) || '––.––'}.`}
                      </p>

                      <label className="mt-4 flex cursor-pointer items-start gap-3" data-id="element-1211" data-mp="nm6bt82rjci9">
                        <input type="checkbox" checked={splitPickup} onChange={e => setSplitPickup(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-rod" data-id="element-1212" data-mp="rmqawh6wcq71" />
                        <span className="text-[0.95rem] leading-snug" data-id="element-1213" data-mp="16b34x8mfbyyw">
                          Jag vill hämta på olika tider
                          <span className="block text-sm text-black/55" data-id="element-1214" data-mp="1bbh564hsa4md">
                            Annars gäller spannet på {PICKUP_WINDOW_MINUTES} minuter ovan.
                          </span>
                        </span>
                      </label>

                      {splitPickup && <div className="mt-5 space-y-4 border-t border-black/15 pt-5" data-id="element-1215" data-mp="3h4fqupzepaxw">
                          {places.map(place => <div key={place.slug} className="flex flex-wrap items-end justify-between gap-4" data-id="element-1216" data-mp="34toiufccnnr1">
                              <label htmlFor={`pickup-${place.slug}`} className="font-display text-lg leading-tight" data-id="element-1217" data-mp="2zubt9mu3g2kr">
                                {place.name}
                              </label>
                              <input id={`pickup-${place.slug}`} type="time" value={pickupTimes[place.slug] ?? ''} onChange={e => setPickupTimes(p => ({
                    ...p,
                    [place.slug]: e.target.value
                  }))} className="w-32 border-b border-black/25 bg-transparent py-2 font-display text-xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-1218" data-mp="3nkfnj3le6pz3" />
                            </div>)}
                        </div>}
                    </> : <p className="mt-2 leading-relaxed text-black/70" data-id="element-1219" data-mp="3taimptjcidq9">
                      Ni hämtar hos {places[0]?.name}. Vi bekräftar adress och tid per mejl.
                    </p>}
                </section>}

              <section data-id="element-1220" data-mp="ur1w01hfuvyo">
                <h2 className={labelClass} data-id="element-1221" data-mp="uph4yluc1v5k">Specialkost</h2>
                <p className="mt-2 text-sm text-black/55" data-id="element-1222" data-mp="g25ukn9w0h71">
                  Bocka i vad som behövs och hur många portioner av beställningen det gäller.
                </p>
                <div className="mt-4 space-y-3" data-id="element-1223" data-mp="kyme146567ds">
                  {DIETS.map(diet => <div key={diet.key} className="flex flex-wrap items-center justify-between gap-4 border-b border-black/15 pb-3" data-id="element-1224" data-mp="18ogr9mudhxyk">
                      <label className="flex cursor-pointer items-start gap-3" data-id="element-1225" data-mp="2y5kn55nbzazd">
                        <input type="checkbox" checked={diets[diet.key].on} onChange={e => setDiets(p => ({
                    ...p,
                    [diet.key]: {
                      on: e.target.checked,
                      count: e.target.checked ? p[diet.key].count : ''
                    }
                  }))} className="mt-1 h-4 w-4 shrink-0 accent-rod" data-id="element-1226" data-mp="2t82lm0bn7y1e" />
                        <span className="text-[0.95rem] leading-snug" data-id="element-1227" data-mp="2o74yrafouftk">
                          {diet.label}
                          <span className="block text-sm text-black/55" data-id="element-1228" data-mp="3j0cwilni4wm">{diet.note}</span>
                        </span>
                      </label>

                      {diets[diet.key].on && <div className="flex items-center gap-2" data-id="element-1229" data-mp="3kfxofvv8qviq">
                          <input type="number" min="1" value={diets[diet.key].count} onChange={e => setDiets(p => ({
                    ...p,
                    [diet.key]: {
                      ...p[diet.key],
                      count: e.target.value
                    }
                  }))} aria-label={`Antal ${diet.label.toLowerCase()}`} placeholder="0" className="w-20 border-b border-black/25 bg-transparent py-1.5 text-center font-display text-xl outline-none transition-colors duration-150 ease-out placeholder:text-black/25 focus:border-rod" data-id="element-1230" data-mp="3kgfqj94l43yk" />
                          <span className="text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-1231" data-mp="lyk07nboazcr">
                            av {lines.reduce((s, l) => s + l.quantity, 0)}
                          </span>
                        </div>}
                    </div>)}
                </div>
              </section>

              <div data-id="element-1232" data-mp="vle9lntz4qz5">
                <label htmlFor="r-notes" className={labelClass} data-id="element-1233" data-mp="voa4t4eft1jw">
                  Övriga allergier och önskemål
                </label>
                <textarea id="r-notes" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Två veganer, en glutenallergi…" className={`${inputClass(false)} resize-none`} data-id="element-1234" data-mp="lwmghcd6g0mn" />
              </div>

              {showErrors && !canContinue && <p role="alert" className="text-sm font-bold uppercase tracking-wide2 text-rod" data-id="element-1235" data-mp="2wytexz3w2m8">
                  Fyll i fälten märkta med *
                </p>}

              <div className="flex flex-wrap gap-4" data-id="element-1236" data-mp="7yfwn052c15z">
                <button onClick={() => setStep(1)} className="border-2 border-black px-8 py-4 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1237" data-mp="29mkbb6npfoh4">
                  Tillbaka
                </button>
                <button onClick={() => canContinue ? (setShowErrors(false), setStep(3)) : setShowErrors(true)} className="group inline-flex items-center gap-3 bg-black px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1238" data-mp="1vw74j7mjuftd">
                  Granska
                  <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1239" data-mp="2ek2bo8nnovdi" />
                </button>
              </div>
            </Reveal>}

          {step === 3 && <Reveal className="space-y-8" data-id="element-1240" data-mp="134drtq5p8cgx">
              <dl className="border-t-2 border-black" data-id="element-1241" data-mp="t2a1bv0b4807">
                {[['Beställare', type === 'foretag' ? 'Företag' : 'Privat'], ['Upplägg', logistics === 'leverans' ? 'Leverans' : 'Upphämtning'], ['Återkommande', frequency === 'lopande' ? cadence : 'Nej, ett tillfälle'], ['Namn', form.name], ['Org.nr', form.org], ['E-post', form.email], ['Telefon', form.phone], ['Datum', form.date], [logistics === 'leverans' ? 'Leveranstid' : 'Upphämtning', logistics === 'leverans' ? form.time.replace(':', '.') : splitPickup && places.length > 1 ? places.map(p => `${p.name} ${(pickupTimes[p.slug] ?? '').replace(':', '.') || '––.––'}`).join(' · ') : `${form.time.replace(':', '.')}–${addMinutes(form.time, PICKUP_WINDOW_MINUTES)}`], ['Adress', logistics === 'leverans' ? form.address : ''], ['Specialkost', activeDiets.map(d => `${d.label} ${diets[d.key].count || '?'} st`).join(' · ')], ['Önskemål', form.notes]].filter(([, v]) => Boolean(v)).map(([label, value]) => <div key={label} className="flex justify-between gap-8 border-b border-black/15 py-3.5" data-id="element-1242" data-mp="z0xngzqobrnc">
                      <dt className="text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-1243" data-mp="1cw9nnmnlmabn">
                        {label}
                      </dt>
                      <dd className="max-w-[60%] text-right" data-id="element-1244" data-mp="kct927ynm2ad">{value}</dd>
                    </div>)}
              </dl>

              <div className="flex flex-wrap gap-4" data-id="element-1245" data-mp="fd6wm0qngq2m">
                <button onClick={() => setStep(2)} className="border-2 border-black px-8 py-4 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1246" data-mp="2r1l84hb0r86r">
                  Tillbaka
                </button>
                <button onClick={submit} className="group inline-flex items-center gap-3 bg-rod px-9 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1247" data-mp="2n11cji63gcmq">
                  Skicka förfrågan
                  <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1248" data-mp="30uag327d23xc" />
                </button>
              </div>
            </Reveal>}
        </div>

        {/* --------------------------------- Basket -------------------------------- */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1249" data-mp="1ua16z2s470sy">
          <div className="border-2 border-black p-6" data-id="element-1250" data-mp="1a8ya5vpfjcse">
            <h2 className="display-xl text-2xl uppercase" data-id="element-1251" data-mp="168e9wj9k7t21">Vad ni får</h2>

            <ul className="mt-6 border-t border-black/15" data-id="element-1252" data-mp="16aip3xcwsehm">
              {lines.map(line => <li key={line.id} className="border-b border-black/15 py-4" data-id="element-1253" data-mp="11fl6byptuqrj">
                  <div className="flex gap-4" data-id="element-1254" data-mp="whtc2y1pmsk6">
                    <img src={line.image} alt="" className="h-16 w-16 shrink-0 object-cover" data-id="element-1255" data-mp="rji2mofbuk2h" />
                    <div className="min-w-0 flex-1" data-id="element-1256" data-mp="mkf0pcun8tyq">
                      <p className="font-display text-lg leading-tight" data-id="element-1257" data-mp="mjnbcjrn3sgw">{line.name}</p>
                      <p className="text-[10px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-1258" data-mp="ijv1m3qifg9y">
                        {line.placeName}
                      </p>
                    </div>
                    <button onClick={() => remove(line.id)} aria-label={`Ta bort ${line.name}`} className="shrink-0 text-black/35 transition-colors duration-150 ease-out hover:text-rod" data-id="element-1259" data-mp="2cx4dg26j3dvd">
                      <Trash2Icon className="h-4 w-4" data-id="element-1260" data-mp="2kg40cht77njh" />
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3" data-id="element-1261" data-mp="2p6kczzp7354s">
                    <div className="flex items-center border border-black/20" data-id="element-1262" data-mp="2u66yewf6froo">
                      <button onClick={() => setQuantity(line.id, line.quantity - 1)} aria-label="Minska" className="flex h-8 w-8 items-center justify-center transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1263" data-mp="2u89rxqfjg2y5">
                        <MinusIcon className="h-3.5 w-3.5" data-id="element-1264" data-mp="2ahxujp49tx9c" />
                      </button>
                      <span className="min-w-[3.5rem] text-center text-sm font-semibold" data-id="element-1265" data-mp="3hw1ctif59ob1">
                        {line.quantity} {line.unit === 'per person' ? 'pers' : 'st'}
                      </span>
                      <button onClick={() => setQuantity(line.id, line.quantity + 1)} aria-label="Öka" className="flex h-8 w-8 items-center justify-center transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1266" data-mp="bt934w33jqkx">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-1267" data-mp="grkdd7xb033o" />
                      </button>
                    </div>
                    <span className="font-display text-lg" data-id="element-1268" data-mp="uip93yxjwb8q">
                      {line.onRequest ? 'Vid förfrågan' : formatPrice(line.price * line.quantity)}
                    </span>
                  </div>
                </li>)}
            </ul>

            <div className="mt-5 space-y-1.5 text-sm" data-id="element-1269" data-mp="zijx9upxymq3">
              <div className="flex justify-between text-black/55" data-id="element-1270" data-mp="zjv3e16v06kd">
                <span data-id="element-1271" data-mp="36ako4r1a6l9i">Exkl. moms</span>
                <span data-id="element-1272" data-mp="36bngz4i5yxs2">{formatPriceExact(total - vat)}</span>
              </div>
              <div className="flex justify-between text-black/55" data-id="element-1273" data-mp="31c12ysuihdi7">
                <span data-id="element-1274" data-mp="1gs5p9ampc4in">Moms {VAT_LABEL}</span>
                <span data-id="element-1275" data-mp="1guifeb2m5l6s">{formatPriceExact(vat)}</span>
              </div>
              <div className="flex items-baseline justify-between border-t border-black/15 pt-3" data-id="element-1276" data-mp="1lq78bvk4o3u4">
                <span className="text-[11px] font-bold uppercase tracking-wide2" data-id="element-1277" data-mp="171t87se3ith9">Preliminärt inkl. moms</span>
                <span className="display-xl text-2xl" data-id="element-1278" data-mp="1c04ho20hb1yy">{formatPrice(total)}</span>
              </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-black/50" data-id="element-1279" data-mp="y40rqkwn1fsa">
              Detta är en förfrågan, inte en bindande order. Slutpris bekräftas av varje aktör.
              {hasOnRequest && ' Produkter med pris vid förfrågan ingår inte i summan.'}
            </p>
          </div>
        </aside>
      </div>
    </div>;
}